<template>
  <master-layout pageTitle="SMS Compliance Keywords">
    <ComplianceKeywords 
      ref="complianceKeywords"
      channel="sms"
      :initialKeywordsData="smsKeywords"
      :messageJsonElements="adMessageJsonElements"
      @refresh="handleRefresh"
      @load-keywords="loadSmsKeywords"
      @keyword-added="handleKeywordAdded"
      @fetch-rule="handleFetchRule"
      @reset-message="resetMessageContent"
    >
     <template #message-content>
      <div class="message-builder-section">
        <JsonBuilderWrapper 
          ref="JsonBuilderWrapper" 
          :editBroadcast="editSMSCompliance" 
          :isViewOnlyMode="false"
          :enableURLUpload="false"
          :hideMessageTypeSelector="true"
          :disableTranscode="false"
          :isSmsCompliance="true"
          @finalJsonElements="handleFinalJsonElements" 
        />
      </div>
      </template>
    </ComplianceKeywords>
  </master-layout>
</template>

<script>
import ComplianceKeywords from "@/components/ComplianceKeywords.vue";
import apiInterceptor from '@/shared/apiInterceptor';
import JsonBuilderWrapper from "@/pages/broadcast/json-builder-wrapper.vue";
import JsonBuilderPreview from "@/pages/broadcast/json-builder-preview.vue";
import { useUIStore } from '@/stores/ui';
import { environment } from '@/shared/env';
import { queryClient } from '@/shared/queryClient';
import { complianceKeywordsByChannelQueryOptions } from '@/composables/useComplianceKeywords';

export default {
  name: 'SmsComplianceKeywords',
  components: { ComplianceKeywords, JsonBuilderWrapper, JsonBuilderPreview },
  data() {
    return {
      smsKeywords: [],
      editSMSCompliance: {
        channel: 'sms'
      },
      adMessageJsonElements: [],
      selectedRule: {}
    }
  },
  async ionViewWillEnter() {
    queryClient.invalidateQueries({ queryKey: complianceKeywordsByChannelQueryOptions('sms').queryKey });
    await this.loadSmsKeywords();
  },
  methods: {
    async handleRefresh() {
      queryClient.invalidateQueries({ queryKey: complianceKeywordsByChannelQueryOptions('sms').queryKey });
      await this.loadSmsKeywords();
    },
    async loadSmsKeywords() {
      try {
        // fetchQuery (not ensureQueryData) ensures a re-fetch when the cache entry
        // is stale (i.e. after invalidateQueries). The SMS-scoped key ['compliance-keywords', 'sms']
        // is independent of the WhatsApp key, so WhatsApp mutations never trigger this fetch.
        const smsCompliance = await queryClient.fetchQuery(complianceKeywordsByChannelQueryOptions('sms'));
        if (Array.isArray(smsCompliance)) {
          // Transform API response to component format
          this.smsKeywords = smsCompliance.map(item => ({
            id: item.id,
            keywords: item.keywords || [],
            type: item.type === 'opt_in' ? 'Opt-in' : 'Opt-out',
            description: item.description,
            active: item.active,
            channel: item.channel,
            seller_id: item.seller_id,
            message: item.message,
            url_types: item.url_types,
            template_data: item.template_data
          }));          
          // Update the ComplianceKeywords component with the loaded data
          this.$refs.complianceKeywords?.updateKeywordsData(this.smsKeywords);
        } else {
          this.smsKeywords = [];
          this.$refs.complianceKeywords?.updateKeywordsData([]);
        }
      } catch (error) {
        this.smsKeywords = [];
        useUIStore().updateToast({ open: true, message: error.message || "Failed to load SMS compliance keywords", type: "danger" })
      }
    },
    
    async handleFetchRule(ruleId) {
      try {
        const response = await apiInterceptor.get( environment("VUE_APP_API_URL") + `social/v1/compliance/${ruleId}` );        
        if (response) {
          const ruleData = {
            id: response.id,
            keywords: response.keywords || [],
            type: response.type === 'opt_in' ? 'Opt-in' : 'Opt-out',
            description: response.description,
            active: response.active,
            channel: response.channel,
            seller_id: response.seller_id,
            message: response.message,
            url_types: response.url_types,
            template_data: response.template_data
          };
          // Update the selected rule in the component
          this.selectedRule = ruleData;
          this.$refs.complianceKeywords?.updateSelectedRule(ruleData);
          // Update the message content for JsonBuilderWrapper
          if (response.message && response.message.message) {
            const messageContent = response.message.message;
            if (messageContent) {
              this.editSMSCompliance = {
                id: response.id,
                channel: 'sms',
                message: response.message,
                template_data: messageContent,
                answers: [
                  {
                    type: 'normalchat',
                    message: messageContent,
                    id: response.message.id
                  }
                ]
              };
              
              // Clear adMessageJsonElements to avoid duplication
              this.adMessageJsonElements = [];
            }
          } else {
            // If no message content, reset to empty
            this.resetMessageContent();
          }
        }
      } catch (error) {
        useUIStore().updateToast({ 
          open: true, 
          message: error.message || "Failed to fetch compliance rule details", 
          type: "danger" 
        });
        this.$refs.complianceKeywords?.stopLoading();
      }
    },
    
    async handleKeywordAdded(data) {
      try {
        // Show loader
        this.$refs.complianceKeywords?.setLoading(true);
        
        // Message content is now optional
        let messageContent = '';
        if (this.adMessageJsonElements && this.adMessageJsonElements.length > 0) {
          messageContent = this.getMessageContent();
        }
        
        let reqBody = {
          keywords: data.keywords,
          type: data.type, // opt_in or opt_out
          description: data.description || "",
          active: data.active,
          channel: data.channel,
          message: messageContent ? {
            "message": [messageContent],
            "template_type": "normalchat"
          } : {},
          url_types: data.url_types || {}
        };
        // Check if we're editing existing keywords (has ID) or creating new ones
        if (data.id) {
          // updating existing compliance keywords
          await apiInterceptor.put( environment("VUE_APP_API_URL") + `social/v1/compliance/${data.id}`, reqBody );
          useUIStore().updateToast({ open: true, message: "Compliance keywords updated successfully", type: "success" });
        } else {
          //  creating new compliance keywords
          await apiInterceptor.post( environment("VUE_APP_API_URL") + "social/v1/compliance", reqBody );
          useUIStore().updateToast({ open: true, message: "Compliance keywords created successfully", type: "success" });
        }
        // Invalidate only the SMS-scoped cache key, then reload.
        // WhatsApp cache is unaffected.
        queryClient.invalidateQueries({ queryKey: complianceKeywordsByChannelQueryOptions('sms').queryKey });
        await this.loadSmsKeywords(); // fetchQuery re-fetches the now-stale SMS cache entry        
        this.$refs.complianceKeywords?.resetToKeywordsTable();
      } catch (error) {
        useUIStore().updateToast({ 
          open: true, 
          message: error.message || "Failed to save compliance keywords", 
          type: "danger" 
        });
      } finally {
        this.$refs.complianceKeywords?.setLoading(false);
      }
    },
    handleFinalJsonElements(data) {
      const finalJsonElements = data.finalJsonElements || data;
      this.adMessageJsonElements = finalJsonElements;
      console.log('SMS compliance message updated:', finalJsonElements);
    },
    // Reset message content for new compliance keywords
    resetMessageContent() {
      this.adMessageJsonElements = [];
      this.editSMSCompliance = {
        channel: 'sms'
      };
    },
    // Get the current message content
    getMessageContent() {
      if (this.adMessageJsonElements && this.adMessageJsonElements.length > 0) {
        const textMessage = this.adMessageJsonElements.find(element => element.type === 'text');
        if (textMessage && textMessage.payload) {
          return textMessage.payload;
        }
      }
      return '';
    },
    // Validate message content
    validateMessageContent() {
      const messageContent = this.getMessageContent();
      if (!messageContent || (typeof messageContent === 'string' && messageContent.trim().length === 0)) {
        useUIStore().updateToast({ 
          open: true, 
          message: "Message content is required for SMS compliance keywords", 
          type: "danger" 
        });
        return false;
      }
      return true;
    }
  }
}
</script>

<style scoped>
.message-builder-section {
  padding: 16px 0;
}
</style>